import React, { FC, useMemo, useCallback } from 'react'
import styled from '@emotion/styled'

import * as mixins from 'view/styles/mixins'
import * as constants from 'view/styles/constants'
import { BorderColourType, getBorderColour } from './rfqPanelStyles'
import { AxeTradeConditionButtons } from 'view/shared/entity'
import { areAllAxed, getCommonTradeConditionFlag, PositionsGrouping, getIsByAssets } from './rfqPanelUtils'
import { iconSet } from 'view/components/icons'
import { CounterpartyInstruction, TradeConditionFlag } from 'api'
import { PreparedStagingOrder } from 'store/selection/selectionTypes'

const IconContainer = styled.div<{ isCollapsed?: boolean }>`
  padding-right: 10px;
  svg {
    color: ${constants.CORE_PRIMARY_4};
    width: 8px;
    height: 8px;
    transform: rotate(${props => (props.isCollapsed ? 0 : '180deg')});
  }
`

const PositionTitle = styled.div`
  display: inline-block;
  font-weight: ${constants.FontWeight.Bold};
  padding-left: 6px;
  padding-right: 7px;
  font-size: 12px;
  text-align: left;
  justify-content: flex-start;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 70%;
`

const PositionItem = styled.div<{ borderColourType: BorderColourType; index: number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-bottom: 1px solid #e1e3e7;
  justify-content: space-between;
  border-left: 6px solid ${props => getBorderColour(props.borderColourType, props.index)};
  &:only-child {
    border-top: none;
    border-bottom: none;
  }
  &:first-of-type {
    border-top: none;
    border-top-left-radius: 6px;
  }
  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: 6px;
  }
`

const PositionHeader = styled.div`
  ${mixins.flexCenterContentCrossAxis};
  height: 44px;
  justify-content: flex-end;
  ${PositionTitle},
  ${IconContainer} {
    cursor: pointer;
  }
  margin-right: 7px;
  font-weight: ${constants.FontWeight.SemiBold};
  font-size: 12px;
`

interface PositionListItemProps {
  ordersToPrepare?: PreparedStagingOrder[]
  id: string
  index: number
  title: string
  readOnly: boolean
  subItems: JSX.Element[]
  chosenGrouping: PositionsGrouping
  counterpartyInstructions: CounterpartyInstruction[]
  toggleItemCollapse: (itemId: string) => void
  getIsItemCollapsed: (itemId: string) => boolean
  onSetAxeTradeCondition: (isAxed: boolean, tradeConditionFlag: TradeConditionFlag, orderIds: string[], counterpartyIds: string[]) => void
}

export const PositionListItem: FC<PositionListItemProps> = props => {
  const {
    id,
    index,
    title,
    subItems,
    readOnly,
    chosenGrouping,
    ordersToPrepare,
    getIsItemCollapsed,
    toggleItemCollapse,
    onSetAxeTradeCondition,
    counterpartyInstructions
  } = props
  const isByAssets = useMemo(() => getIsByAssets(chosenGrouping), [chosenGrouping])
  const counterpartyIds = useMemo(() => counterpartyInstructions.map(instruction => instruction.counterpartyId), [counterpartyInstructions])
  const border = useMemo(() => (isByAssets ? BorderColourType.Asset : BorderColourType.Counterparty), [isByAssets])

  const updateAll = useCallback(
    (isAxed: boolean, tradeConditionFlag: TradeConditionFlag) => {
      if (ordersToPrepare && !isByAssets) {
        const ids = ordersToPrepare.map(order => order.orderDetail.order.orderId)
        onSetAxeTradeCondition(isAxed, tradeConditionFlag, ids, [id])
      } else {
        onSetAxeTradeCondition(isAxed, tradeConditionFlag, [id], counterpartyIds)
      }
    },
    [counterpartyIds, ordersToPrepare, id, isByAssets]
  )

  return (
    <PositionItem borderColourType={border} index={index} data-test-id={'SendRFQPanel-PositionListItem'}>
      <PositionHeader>
        <PositionTitle data-test-id={'SendRFQPanel-PositionListItem-Title'} onClick={() => toggleItemCollapse(id)}>
          {title}
        </PositionTitle>
        {counterpartyIds.length > 0 && (
          <IconContainer onClick={() => toggleItemCollapse(id)} isCollapsed={getIsItemCollapsed(id)}>
            {iconSet.chevronDown}
          </IconContainer>
        )}
        <AxeTradeConditionButtons
          dataTestId={'SendRFQPanel-PositionListItem-AxeTradeConditionButtons'}
          isAxed={areAllAxed(counterpartyInstructions)}
          tradeConditionFlag={getCommonTradeConditionFlag(counterpartyInstructions)}
          readOnly={readOnly}
          onUpdate={(isAxed, tradeConditionFlag) => updateAll(isAxed, tradeConditionFlag)}
        />
      </PositionHeader>
      {!getIsItemCollapsed(id) && subItems}
    </PositionItem>
  )
}
